// react-routers components
import { Link } from "react-router-dom";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import moment from "moment";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";

import { useEffect, useState } from "react";

import { useAuth } from "context/AuthContext";
import { getStage } from "api/stages";
import { getBillings } from "api/billings";
import { getDevices } from "api/devices";
import { getReserves } from "api/reserves";
import { areaInv, areaRes, deadlines } from "config/enum";
import { useDispatch, useSelector } from "react-redux";
import { setMyOrders } from "../../../redux/reducers/filtersReducer";

function ProfilesList({ title, shadow }) {
  const dispatch = useDispatch();
  const myOrders = useSelector((state) => state.filters.myOrders);
  const [billings, setBillings] = useState([]);
  const [devices, setDevices] = useState([]);
  const [stages, setStages] = useState([]);
  const [reserves, setReserves] = useState([]);
  const [client, setCient] = useState(0);
  const [ce, setCE] = useState(0);
  const { user } = useAuth();

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  async function fetchBillings() {
    const response = await getBillings(myOrders);
    setBillings(response.data);
  }

  // Function to fetch stages and calculate hasRessourcesAlert
  async function fetchStagesAndCheckAlert(device) {
    const stagesResponse = await getStage(device.device_id);
    const stages = stagesResponse.data;

    const now = moment();
    const hasRessourcesAlert = stages.filter((stage) => {
      const startWithinTwoWeeks = moment(stage.stage_start_date).isBetween(
        now,
        moment().add(deadlines["stage"], "weeks")
      );
      const noResources =
        !stage.ressource1_id &&
        !stage.ressource2_id &&
        !stage.ressource3_id &&
        !stage.ressource4_id;
      return startWithinTwoWeeks && noResources;
    }).length;

    return hasRessourcesAlert;
  }

  function addWeeks(date, weekNumber) {
    var dat = new Date(date.valueOf());
    dat.setDate(dat.getDate() + 7 * weekNumber);
    return dat;
  }

  async function fetchDevicesAndReserves() {
    const devicesResponse = await getDevices();
    const reservesResponse = await getReserves();

    const devicesWithAlertsOrMissingInfo = await Promise.all(
      devicesResponse.data.map(async (device) => {
        const missingInfoDetails = getMissingInfoDetails(device);
        const hasRessourcesAlert = await fetchStagesAndCheckAlert(device);
        const hasMadContractualDateAlert = !device.mad_contractual_date;
        const hasAssContractualDateAlert = !device.assembly_start_contractual_date;
        const hasMissingWait = !device.wait;
        const hasMissingPeriod = !device.period;
        const isMadContractualDateWithinTwoWeeks =
          device.mad_contractual_date &&
          moment(device.mad_contractual_date).diff(moment(), "weeks") < deadlines["reserve"];

        // Calculate the delivery date
        const deliveryDate =
          device.site_delivery_real_date !== null
            ? moment(device.site_delivery_real_date)
            : device.ex_factory_real_date
            ? moment(addWeeks(device.ex_factory_real_date, 1))
            : moment.invalid();

        // Check if delivery is planned after assembly start, and ensure assembly_start_real_date is valid
        const deliveryAfterAssemblyStart =
          device.assembly_start_real_date &&
          deliveryDate.isValid() &&
          moment(device.assembly_start_real_date).isValid() &&
          deliveryDate.isAfter(moment(device.assembly_start_real_date));

        // Check if delivery is within the next week and there is no assembly_start_real_date
        const deliveryInLessThanAWeek =
          !device.assembly_start_real_date &&
          deliveryDate.isValid() &&
          deliveryDate.isBefore(moment().add(1, "weeks"));

        return {
          ...device,
          ...missingInfoDetails,
          hasAlerts:
            device.plan_alert ||
            device.cabin_alert ||
            device.ex_factory_alert ||
            device.maintenance_contract_alert ||
            device.ce_alert ||
            device.safety_study_alert ||
            hasRessourcesAlert > 0 ||
            deliveryAfterAssemblyStart ||
            deliveryInLessThanAWeek,
          isMadContractualDateWithinTwoWeeks,
          hasRessourcesAlert,
          hasMissingPeriod,
          hasMissingWait,
          hasAssContractualDateAlert,
          hasMadContractualDateAlert,
          deliveryAfterAssemblyStart, // Already existing alert logic
          deliveryInLessThanAWeek, // New alert logic
        };
      })
    );

    const filteredDevices = devicesWithAlertsOrMissingInfo.filter(
      (device) =>
        device.isMissing ||
        device.hasAlerts ||
        device.hasRessourcesAlert > 0 ||
        device.hasMissingPeriod ||
        device.hasMissingWait ||
        device.hasAssContractualDateAlert ||
        device.hasMadContractualDateAlert ||
        device.deliveryAfterAssemblyStart ||
        device.deliveryInLessThanAWeek // Include this in your filtering condition
    );

    setReserves(reservesResponse.data);
    setDevices(filteredDevices);
  }

  useEffect(() => {
    if (user && user.role === "Assistante de gestion") {
      fetchBillings();
    }
    if (user && (user.role === "Contremaître" || user.role === "Admin" || user.role === "Chef d'agence")) {
      fetchDevicesAndReserves();
    }
  }, [user, myOrders]);

  const groupBillingsByOrder = (billings) => {
    return billings.reduce((acc, billing) => {
      const orderId = billing.order_id;
      const deviceId = billing.device_id;

      if (!acc[orderId]) {
        acc[orderId] = {
          order_id: orderId,
          site_name: billing.site_name,
          area: billing.area,
          billingsByDevice: {},
        };
      }

      if (!acc[orderId].billingsByDevice[deviceId]) {
        acc[orderId].billingsByDevice[deviceId] = {
          device_id: deviceId,
          device_number: billing.device_number,
          area: billing.area,
          order_id: billing.order_id,
          status: billing.status,
          count: 0,
        };
      }

      acc[orderId].billingsByDevice[deviceId].count++;
      return acc;
    }, {});
  };

  const mandatoryFields = {
    "Ascenseur de charge": ["payload", "level_number", "faces_number", "pylon", "location"],
    Ascenseur: ["payload", "level_number", "faces_number", "pylon", "location"],
    EPMR: ["payload", "level_number", "faces_number", "pylon", "location"],
    Chaise: ["payload", "level_number", "configuration", "faces_number", "location"],
    "Monte-charge accessible": ["payload", "level_number", "faces_number", "pylon", "location"],
    "Monte-charge accompagné": ["payload", "level_number", "faces_number", "pylon", "location"],
    "Monte-charge non-accompagné": ["payload", "level_number", "faces_number", "pylon", "location"],
  };

  function getMissingInfoDetails(device) {
    const fieldsToCheck = mandatoryFields[device.type];
    if (!fieldsToCheck) {
      return { isMissing: false, missingCount: 0 };
    }

    let missingCount = 0;
    fieldsToCheck.forEach((field) => {
      const value = device[field];
      if (value === null || value === "") {
        missingCount++;
      }
    });
    return { isMissing: missingCount > 0, missingCount };
  }

  // Group the billings by order_id
  const groupedBillings = groupBillingsByOrder(billings);

  // Updated renderBillings to use groupedBillings
  const renderGroupedBillings = (groupedBillings) => {
    return Object.values(groupedBillings).map((group) => (
      <MDBox
        key={`order-${group.order_id}`}
        component="li"
        display="flex"
        alignItems="center"
        pt={1}
        pb={2}
      >
        <MDBox mr={2}>
          <MDAvatar src={areaInv[group.area]} size="lg" alt="something here" />
        </MDBox>
        <MDBox component="div">
          <MDTypography variant="h6" fontWeight="bold">
            {group.site_name}
          </MDTypography>
          {Object.values(group.billingsByDevice).map((device) => (
            <MDBox key={device.device_id} component="div">
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  <MDTypography variant="button" fontWeight="medium">
                    Appareil {device.device_number} ({device.area})
                  </MDTypography>
                  <MDTypography pl={1} variant="caption">
                    <b>{device.count > 1 ? `${device.count} factures` : "1 facture"}</b> en statut "
                    {device.status}"
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
        <MDBox ml="auto">
          <MDButton
            component={Link}
            to={`/commandes/${group.order_id}`}
            variant=""
            color={darkMode ? "dark" : "white"}
            sx={{ color: darkMode ? "white !important" : "#344767 !important" }}
          >
            Voir la commande
          </MDButton>
        </MDBox>
      </MDBox>
    ));
  };

  // Function to group reserves by device_id and reserve_type
  const groupReservesByDevice = (reservesArray) => {
    return reservesArray.reduce((acc, reserve) => {
      const deviceId = reserve.device_id;
      if (!acc[deviceId]) {
        acc[deviceId] = {
          device_id: reserve.device_id,
          device_number: reserve.device_number,
          area: reserve.area,
          organism: 0,
          client: 0,
          order_id: reserve.order_id, // Keep the most recent order_id
        };
      }
      if (reserve.reserve_type === "Organism") {
        acc[deviceId].organism++;
      } else if (reserve.reserve_type === "Client") {
        acc[deviceId].client++;
      }
      acc[deviceId].order_id = reserve.order_id; // Update to the most recent order_id
      return acc;
    }, {});
  };

  // Grouped reserves
  const groupedReserves = groupReservesByDevice(reserves);

  useEffect(() => {
    Object.values(groupedReserves).map((reserve) => {
      setCient(reserve.client);
      setCE(reserve.organism);
    });
  }, [groupedReserves]);

  const unifiedDevices = devices.map((device) => {
    const reserveInfo = groupedReserves[device.device_id] || { organism: 0, client: 0 };
    const missingInfoDetails = getMissingInfoDetails(device);
    return {
      ...device,
      ...reserveInfo,
      ...missingInfoDetails,
      key: `device-${device.device_id}`,
    };
  });

  Object.values(groupedReserves).forEach((reserve) => {
    if (!unifiedDevices.some((device) => device.device_id === reserve.device_id)) {
      unifiedDevices.push({
        ...reserve,
        key: `device-${reserve.device_id}`,
        isMissing: false,
        missingCount: 0,
        organism: reserve.organism,
        client: reserve.client,
      });
    }
  });

  const groupDevicesByOrder = (unifiedDevices) => {
    return unifiedDevices.reduce((acc, device) => {
      const orderId = device.order_id;
      if (!acc[orderId]) {
        acc[orderId] = {
          order_id: orderId,
          site_name: device.site_name,
          area: device.area,
          devices: [],
        };
      }
      acc[orderId].devices.push(device);
      return acc;
    }, {});
  };

  const groupedDevices = groupDevicesByOrder(devices);

  const renderGroupedNotifications = (groupedDevices) => {
    return Object.values(groupedDevices).map((group) => (
      <MDBox
        key={`order-${group.order_id}`}
        component="li"
        display="flex"
        alignItems="center"
        pt={1}
        pb={2}
      >
        <MDBox mr={2}>
          <MDAvatar src={areaRes[group.area]} size="lg" alt="something here" />
        </MDBox>
        <MDBox component="div">
          <MDTypography variant="h6" fontWeight="bold">
            {group.site_name}
          </MDTypography>
          {group.devices.map((device) => (
            <MDBox key={device.device_id} component="div">
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  <MDTypography variant="button" fontWeight="medium">
                    Appareil {device.device_number} ({device.area})
                  </MDTypography>
                  {device.missingCount > 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>
                        {device.missingCount > 1
                          ? `${device.missingCount} informations techniques`
                          : "1 information technique"}
                      </b>{" "}
                      manquante{device.missingCount > 1 ? "s" : ""}
                    </MDTypography>
                  )}
                  {(device.hasMadContractualDateAlert || device.hasAssContractualDateAlert) &&
                    (device.hasMadContractualDateAlert && !device.hasAssContractualDateAlert ? (
                      <MDTypography pl={1} variant="caption">
                        <b>Date contractuelle de MAD</b> non renseignée (préparation du chantier)
                      </MDTypography>
                    ) : device.hasAssContractualDateAlert && !device.hasMadContractualDateAlert ? (
                      <MDTypography pl={1} variant="caption">
                        <b>Date contractuelle de début du montage</b> non renseignée (préparation du
                        chantier)
                      </MDTypography>
                    ) : (
                      <MDTypography pl={1} variant="caption">
                        <b>Dates contractuelles</b> non renseignées (préparation du chantier)
                      </MDTypography>
                    ))}
                  {device.hasContractualDatesAlert && (
                    <MDTypography pl={1} variant="caption">
                      <b>Dates contractuelles</b> non renseignées (préparation du chantier)
                    </MDTypography>
                  )}
                  {device.hasMissingWait && (
                    <MDTypography pl={1} variant="caption">
                      <b>Délai fabrication appareil</b> non renseigné dans les informations de
                      l'appareil
                    </MDTypography>
                  )}
                  {device.hasMissingPeriod && (
                    <MDTypography pl={1} variant="caption">
                      <b>Durée du chantier</b> non renseignée dans les informations de l'appareil
                    </MDTypography>
                  )}
                  {device.deliveryAfterAssemblyStart && (
                    <MDTypography pl={1} variant="caption">
                      <b>Livraison sur site</b> après le début de montage
                    </MDTypography>
                  )}
                  {device.deliveryInLessThanAWeek && (
                    <MDTypography pl={1} variant="caption">
                      <b>Début de montage</b> non renseignée (livraison sur site dans moins d'une
                      semaine)
                    </MDTypography>
                  )}
                  {device.hasRessourcesAlert > 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>
                        {device.hasRessourcesAlert}{" "}
                        {device.hasRessourcesAlert > 1 ? "phases" : "phase"} de chantier
                      </b>{" "}
                      sans ressource (dans moins de {`${deadlines["stage"]}`} semaines)
                    </MDTypography>
                  )}
                  {(device.client > 0 || device.organism > 0) &&
                    device.isMadContractualDateWithinTwoWeeks && (
                      <MDTypography pl={1} variant="caption">
                        <b>
                          {device.organism > 0 &&
                            `${device.organism} ${
                              device.organism === 1 ? "réserve" : "réserves"
                            } CE `}
                        </b>
                        {device.client > 0 && device.organism > 0 ? "et " : ""}
                        <b>
                          {device.client > 0 &&
                            `${device.client} ${
                              device.client === 1 ? "réserve" : "réserves"
                            } Client `}
                        </b>
                        {client + ce > 1
                          ? "non levées (MAD dans moins de " + deadlines["reserve"] + " semaines)"
                          : "non levée (MAD dans moins de " + deadlines["reserve"] + " semaines)"}
                      </MDTypography>
                    )}
                  {device.plan_alert !== 0 && device.cabin_alert !== 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>Plans</b> et <b>finitions cabine</b> à valider (MAD dans moins de{" "}
                      {`${deadlines["plan"]}`} semaines)
                    </MDTypography>
                  )}
                  {device.plan_alert === 0 && device.cabin_alert !== 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>Finitions cabine</b> à valider (MAD dans moins de {`${deadlines["cabin"]}`}{" "}
                      semaines)
                    </MDTypography>
                  )}
                  {device.plan_alert !== 0 && device.cabin_alert === 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>Plans</b> à valider (MAD dans moins de {`${deadlines["plan"]}`} semaines)
                    </MDTypography>
                  )}
                  {device.ex_factory_alert !== 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>Sortie d'usine</b> urgente (début du montage dans moins de{" "}
                      {`${deadlines["factory"]}`} semaine)
                    </MDTypography>
                  )}
                  {device.maintenance_contract_alert !== 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>Contrat de maintenance</b> à signer (MAD dans moins de{" "}
                      {`${deadlines["maintenance"]}`} semaines)
                    </MDTypography>
                  )}
                  {device.ce_alert !== 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>CE</b> à prévoir (MAD dans moins de {`${deadlines["ce"]}`} semaines)
                    </MDTypography>
                  )}
                  {device.safety_study_alert !== 0 && (
                    <MDTypography pl={1} variant="caption">
                      <b>Étude de sécurité</b> à réaliser (MAD dans moins de{" "}
                      {`${deadlines["safety"]}`} semaines)
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
        <MDBox ml="auto">
          <MDButton
            component={Link}
            to={`/commandes/${encodeURIComponent(group.order_id)}`}
            variant=""
            color={darkMode ? "dark" : "primary"}
            size="small"
          >
            Voir la commande
          </MDButton>
        </MDBox>
      </MDBox>
    ));
  };

  const finalNotifications = renderGroupedNotifications(groupedDevices);
  const finalBillings = renderGroupedBillings(groupedBillings);

  // Combine with billings for the final list
  const finalCombinedList = [
    ...finalBillings.map((element) => ({ ...element, key: `billing-${element.order_id}` })),
    ...finalNotifications,
  ];

  return (
    <Card sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      <MDBox pt={2} px={2}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <MDTypography variant="h6" fontWeight="medium">
              {title}
            </MDTypography>
          </Grid>
          {user && (user.role === "Assistante de gestion" || user.role === "Admin") && (
            <Grid sx={{ marginTop: -0.7 }} item>
              <MDButton
                onClick={() => {
                  dispatch(setMyOrders(!myOrders));
                }}
                value={myOrders}
                variant="contained"
                sx={{
                  color: !darkMode ? "white" : "#1A2035",
                  boxShadow:
                    darkMode &&
                    "0rem 0.1875rem 0.1875rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(255, 255, 255, 0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(255, 255, 255, 0.15)",
                  "&:hover": {
                    boxShadow:
                      darkMode &&
                      "0rem 0.875rem 1.625rem -0.75rem rgba(255, 255, 255, 0.4), 0rem 0.25rem 1.4375rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(255, 255, 255, 0.2)",
                  },
                }}
                color={darkMode ? "white" : "dark"}
                size="small"
              >
                {myOrders ? "Vos affaires attitrées" : "Toutes les affaires"}
              </MDButton>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <MDBox p={2}>
        {finalCombinedList.length > 0 ? (
          finalCombinedList
        ) : (
          <MDBox
            mt={2.5}
            sx={{
              textAlign: "center",
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MDTypography component="a" color="text" fontWeight="light">
              Aucun impératif à afficher 🙂
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

export default ProfilesList;
